import { Label } from "../ui/label";
import { Button } from "../ui/button";
import LoadingButton from "../LoadingButton";
import { Input } from "../ui/input";
import { useState } from "react";
import {
  Toast,
  ToastDescription,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";
import { format } from "date-fns";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { CalendarIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const CreateCoupon = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [toastList, setToastList] = useState([]);

  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const [couponDetails, setCouponDetails] = useState({
    name: "",
    description: "",
    discountAmount: "",
    usageStartDate: "",
    usageEndDate: "",
    isOneTimeUsage: false,
  });

  const handleInputChange = (e) => {
    const { id, value, type } = e.target;
    setCouponDetails((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? e.target.checked : value, // Dynamically update the corresponding key
    }));
  };

  const handleDateChange = (id, date) => {
    setCouponDetails((prev) => ({
      ...prev,
      [id]: date ? format(date, "yyyy-MM-dd") : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      const token = localStorage.getItem("x-auth-token");
      //   console.log(token);
      const response = await fetch(`${API_URL}/api/coupon/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
        body: JSON.stringify(couponDetails),
      });

      const responseData = await response.json();

      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data?.message || "",
            variant: "default",
          },
        ]);

        setIsLoading(false);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data?.message || "Something went wrong!",
            variant: "default",
          },
        ]);
        // console.error("Failed to update cost:", responseData.error);

        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error updating cost:", error);
      setIsLoading(false);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <main className="tw-p-8">
      <form onSubmit={handleSubmit}>
        <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8">
          <div className="tw-grid tw-gap-2">
            <Label className="tw-font-medium" htmlFor="name">
              Name
            </Label>
            <Input
              className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs tw-bg-white placeholder:md:tw-text-sm"
              id="name"
              type="text"
              value={couponDetails.name}
              onChange={handleInputChange}
              placeholder="Enter name"
              required
            />
          </div>

          <div className="tw-grid tw-gap-2">
            <Label className="tw-font-medium" htmlFor="description">
              Description
            </Label>
            <Input
              className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs tw-bg-white placeholder:md:tw-text-sm"
              id="description"
              type="text"
              value={couponDetails.description}
              onChange={handleInputChange}
              placeholder="Enter description"
              required
            />
          </div>

          <div className="tw-grid tw-gap-2">
            <Label className="tw-font-medium" htmlFor="discountAmount">
              Discount Amount
            </Label>
            <Input
              className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs no-spin tw-bg-white placeholder:md:tw-text-sm"
              id="discountAmount"
              type="number"
              value={couponDetails.discountAmount}
              onChange={handleInputChange}
              placeholder="Enter discount amount"
              required
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault(); // Prevent increment/decrement
                }
              }}
            />
          </div>

          <div className="tw-grid tw-gap-4">
            {/* Start Date Picker */}
            <div className="tw-grid tw-gap-2">
              <Label htmlFor="usageStartDate" className="tw-font-medium">
                Start Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="tw-w-full tw-text-sm tw-justify-start tw-text-left tw-font-normal tw-border tw-border-[#00B5ED] tw-bg-white"
                  >
                    <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4 tw-text-[#00B5ED]" />
                    {couponDetails.usageStartDate ? (
                      format(new Date(couponDetails.usageStartDate), "PPP")
                    ) : (
                      <span>Pick a start date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="tw-w-auto tw-p-0">
                  <Calendar
                    mode="single"
                    selected={
                      couponDetails.usageStartDate
                        ? new Date(couponDetails.usageStartDate)
                        : undefined
                    }
                    onSelect={(date) =>
                      handleDateChange("usageStartDate", date)
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date Picker */}
            <div className="tw-grid tw-gap-2">
              <Label htmlFor="usageEndDate" className="tw-font-medium">
                End Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="tw-w-full tw-text-sm tw-justify-start tw-text-left tw-font-normal tw-border tw-border-[#00B5ED] tw-bg-white"
                  >
                    <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4 tw-text-[#00B5ED]" />
                    {couponDetails.usageEndDate ? (
                      format(new Date(couponDetails.usageEndDate), "PPP")
                    ) : (
                      <span>Pick an end date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="tw-w-auto tw-p-0">
                  <Calendar
                    mode="single"
                    selected={
                      couponDetails.usageEndDate
                        ? new Date(couponDetails.usageEndDate)
                        : undefined
                    }
                    onSelect={(date) => handleDateChange("usageEndDate", date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <input
              id="isOneTimeUsage"
              type="checkbox"
              checked={couponDetails.isOneTimeUsage}
              onChange={handleInputChange}
              className="tw-appearance-none tw-w-4 tw-h-4 tw-border-2 tw-border-[#00B5ED] tw-rounded-sm tw-bg-white tw-cursor-pointer 
               checked:tw-bg-[#00B5ED] checked:tw-border-[#00B5ED] checked:tw-text-white checked:tw-content-['✔'] 
               tw-flex tw-items-center tw-justify-center focus:tw-ring-2 focus:tw-ring-[#00B5ED]"
            />
            <Label htmlFor="isOneTimeUsage">One-Time Use</Label>
          </div>
        </div>

        {isLoading ? (
          <LoadingButton />
        ) : (
          <Button
            type="submit"
            className="tw-w-full tw-font-semibold tw-mt-6 md:tw-mt-8 tw-text-sm"
          >
            CONFIRM
          </Button>
        )}
      </form>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </main>
  );
};

export default CreateCoupon;
